import { Component, ElementRef, ViewChild } from '@angular/core';
import { Tree } from 'primeng/tree';
import { ComponentView } from 'src/modules/app-template/models/component-view.model';
import { DeploymentOptionsDocumentHandler } from 'src/modules/enaio/shared/DeploymentOptionsDocumentHandler';
import { DocumentHandlerTaskStatus } from 'src/modules/enaio/shared/DocumentHandlerTaskStatus';
import { UpdatePackageMetaData } from 'src/modules/enaio/shared/UpdatePackageMetaData';
import { SmTimer } from 'src/modules/sm-base/models/sm-timer.model';
import { GuiUtils } from 'src/modules/utils/misc/gui-utils';
import { TreeSelectionHelper } from 'src/modules/utils/misc/tree-selection-helper';
import { Progressor } from 'src/modules/utils/misc/Progressor';
import { Utils } from 'src/modules/utils/shared/utils';
import { Wrapper } from 'src/modules/utils/misc/wrapper.model';
import { DhCustomer } from '../../models/dh-customer.model';
import { DhTools } from '../../models/dh-tools.model';
import { AttachedDocumentHandlerProcess } from 'src/modules/enaio/shared/AttachedDocumentHandlerProcess';
import { DhUpdateFileDto } from 'src/modules/enaio/shared/DhUpdateFileDto';
import { FrontendFormDefinition } from 'src/modules/sm-base/models/frontend-form-definition.model';
import { FrontendFieldDefinition } from 'src/modules/sm-base/models/frontend-field-definition.model';
import { FrontendFieldType } from 'src/modules/sm-base/models/frontend-field-type.enum';
import { FrontendFieldListItem } from 'src/modules/sm-base/models/frontend-field-list-item.model';

@Component({
    selector: 'app-document-handler-updater',
    templateUrl: './document-handler-updater.component.html',
    styleUrls: ['./document-handler-updater.component.scss']
  })
  export class DocumentHandlerUpdaterComponent extends ComponentView {

    _Utils = Utils;

    customers: DhCustomer[];
    selectedCustomer = "";

    progressor = new Wrapper<Progressor>();
    devMode = false;
    devMode2 = false;
    metaData: UpdatePackageMetaData;
    changesTree: TreeSelectionHelper;
    waitText: string = null;
    fileName = "";
    selFileLeft: DhUpdateFileDto;
    selFileRight: DhUpdateFileDto;
    taskStatusTimer: SmTimer;

    @ViewChild("tree") tree: Tree;
    @ViewChild("main", {read: ElementRef}) private mainRef: ElementRef;

    async initParams(): Promise<boolean> {
        this.addKeyboardListener("Ctrl-S", event => {
            if (GuiUtils.isComponentVisible(this.mainRef)) {
                this.devMode = true;
                Utils.setTimerOnce(60_000, () => {
                    this.devMode = false;
                    this.devMode2 = false;
                });
                event.preventDefault();
            }
        });
        this.addKeyboardListener("Ctrl-Alt-Ä", event => {
            if (this.devMode) {
                this.devMode2 = true;
                event.preventDefault();
            }
        });

        this.customers = await DhTools.backendCall("api/dh/listCustomers").list(DhCustomer);
        this.selectedCustomer = this.customers.some(c => c.name == "sm") ? "sm" : Utils.arrayGetSafe(this.customers.filter(c => c.name != "tools"), 0)?.name ?? "tools";

        return Promise.resolve(true);
    }

    async copyConfigToClipboard(): Promise<void> {
        await DhTools.backendCall("api/dh/updaterCopyConfigToClipboard").getText();
        this.app.showToast("success", "Erfolgreich", "Der Config-Ordner befindet sich in der Zwischenablage");
    }

    async copyConfigToDev(): Promise<void> {
        await DhTools.backendCall("api/dh/updaterCopyConfigToDev").getText();
        this.app.showToast("success", "Erfolgreich", "Der Config-Ordner befindet sich in der Zwischenablage");
    }

    async getUpdate(): Promise<void> {
        let options = Utils.fromPlain(DeploymentOptionsDocumentHandler, {
            customer: this.selectedCustomer,
            updateId: Utils.generateGuid()
        });
        DhTools.startProgressor(this.progressor, await DhTools.backendCall("api/dh/updaterGenerateUpdate", { options }).getText(),
        async result => this.updateArrived(Utils.fromPlainUnsafe(UpdatePackageMetaData, Utils.fromJson(result.value))),
        result => this.app.showToast("error", "Das Update ist fehlgeschlagen", result));
    }

    async getFile(): Promise<void> {
        let format = await this.app.messageDialog.input<string>(
            new FrontendFieldDefinition("format", "Format", FrontendFieldType.comboBox, { 
                mandatory: true,
                dropdownEditable: false,
                listItems: FrontendFieldListItem.make(["pdf", "org", "base64", "html"], item => [item, item]),
                value: "pdf"
            }), "Format"
        );
        await DhTools.backendCall("api/dh/updaterGetFile", { format }).getText();
    }

    async updateArrived(metaData: UpdatePackageMetaData): Promise<void> {
        this.metaData = metaData;//
        this.waitText = "Rufe Aufgabenplanung ab ...";
        this.taskStatusTimer = this.addTimer(new SmTimer(0, 1000, async () => {
            let status = await DhTools.backendCall("api/dh/getTaskStatus").list(DocumentHandlerTaskStatus);
            let runningProcesses = await DhTools.backendCall("api/dhinstance/getRunning").list(AttachedDocumentHandlerProcess);

            let statusText = Utils.arrayWithoutNull([
                ...status.map(s => s.isRunning ? s.name + " (Läuft)" :  Utils.dateDiffSeconds(new Date(), s.nextRun) <= 10 && Utils.dateDiffSeconds(new Date(), s.nextRun) >= -10 ? s.name + " (Startet in " + Utils.dateDiffSeconds(new Date(), s.nextRun) + " Sekunden)" : null),
                ...runningProcesses.map(s => "Prozess " + s.processId + " (Läuft)")
            ]);
            this.waitText = statusText.length == 0 ? null : Utils.arrayItemsToString(statusText, ", ");
        }, true));
        this.changesTree = new TreeSelectionHelper(this.tree);
        this.changesTree.setItems(GuiUtils.generateTree(this.metaData.files,
        file => [file.name.toLowerCase().startsWith("config") ? "Konfigurationen" : "Weitere Dateien", file.type == "d" ? "Gelöscht" : file.type == "u" ? "Aktualisiert" : "Neu", file.name],
        (file, key, index) => ({ label: Utils.toString(key[index]), data: key[index], expanded: index < 2 }), true));
        
        let allSafe = !this.metaData.files.some(file => file.type == "d" || file.name.toLowerCase().startsWith("config"));
        if (allSafe) {
            this.app.showToast("info", "Info", "Update wird ohne Rückfrage eingespielt, da es keine unsicheren Änderungen gibt");
            await this.runUpdate(true);
            return;
        }

        this.changesTree.setSelected(this.metaData.files.filter(file => file.type != "d" && !file.name.toLowerCase().startsWith("config")));
    }

    async runUpdate(all?: boolean): Promise<void> {
        this.taskStatusTimer?.destroy();
        await DhTools.backendCall("api/dh/updaterRunUpdate", { id: this.metaData.id, fileNames: all ? [...this.metaData.files.map(file => file.name)] : this.changesTree.getSelected() }).getText();
        this.updateFinished();
    }

    updateFinished(): void {
        this.cancelUpdate();
        this.app.showToast("success", "Erfolgreich", "Das Update wird eingespielt ...");
    }

    cancelUpdate(): void {
        this.clearTimers();
        this.metaData = null;
        this.changesTree = null;
        this.waitText = null;
        this.fileName = "";
        this.selFileLeft = null;
        this.selFileRight = null;
    }

    async selectFile(event: any): Promise<void> {
        if ((event.node.label as string).toLowerCase().startsWith("config")) {
            this.fileName = event.node.label;
            [this.selFileRight, this.selFileLeft] = await DhTools.backendCall("api/dh/updaterGetFileContents", { id: this.metaData.id, fileName: this.fileName, maxLength: 20_000 }).list(DhUpdateFileDto);
        }
        else {
            this.selFileLeft = null;
            this.selFileRight = null;
        }
    }
}
